import { FunctionComponent } from "react";
import { useTranslation } from "react-i18next";

import { buildListScopeCrossLink, ListScope } from "../../components/ListScopeFilter/listScope";

import type { CustomListsAutocompleteForSequencerItem } from "../../../List/components/CustomListsAutocompleteForSequencer";

interface InboxEmptyScopeProps {
	/** The list filter currently applied to the Inbox. */
	selectedLists: CustomListsAutocompleteForSequencerItem[];
}

/**
 * Shown when the Inbox has no conversations. With a list filter applied this is the second half of
 * PREC-3834: picking a list that holds no replies must say so — and point at the Outbox where those
 * leads actually are — instead of rendering a blank column that reads as a broken filter.
 */
export const InboxEmptyScope: FunctionComponent<InboxEmptyScopeProps> = ({ selectedLists }) => {
	const { t } = useTranslation("translation", { keyPrefix: "list.scopeFilter.irrelevant.inbox" });

	const crossLinkHref = buildListScopeCrossLink(ListScope.INBOX, selectedLists);

	return (
		<div
			className="flex flex-col items-center gap-2 px-6 py-10 text-center"
			data-test="inbox-empty-scope"
		>
			<p className="text-sm font-medium text-gray-900">
				{selectedLists.length === 1
					? t("emptyForList", { list: selectedLists[0].title })
					: t("emptyTitle")}
			</p>
			<p className="text-xs text-text-meta">{t("hint")}</p>
			{crossLinkHref && (
				<a
					className="text-xs font-medium text-brand-light cursor-pointer hover:underline"
					data-test="inbox-empty-scope-cross-link"
					href={crossLinkHref}
				>
					{t("crossLink")}
				</a>
			)}
		</div>
	);
};
